import { useKioskStore } from '@/stores/kiosk';
import { useListStore } from '@/stores/list';
import { TNullable } from '@/types';
import { getNowInTimezone, getWallTimeInTimezone } from '@/utils/convertDate';
import { isPropertyOf } from '@/utils/isPropertyOf';
import { useCallback, useEffect, useState } from 'react';
import { WARNING_TIME_THRESHOLD } from '../constants';
import { useInterval } from '@/hooks/useInterval';
import {
  KIOSK_AVAILABILITY_RECHECK_INTERVAL,
  weekdayStringToDayjsNumberMap,
} from '@/constants';

/**
 * This hook checks the kiosk's availability based on its schedule and manages a timer that counts down
 * to when the kiosk will become unavailable. It updates the state to block dashboard buttons when the
 * kiosk is about to become unavailable and unblocks them when it becomes available again.
 * @returns The remaining time in seconds until the kiosk becomes unavailable, or null if the kiosk is available.
 */
export const useKioskAvailabilityOverride = () => {
  const [time, setTime] = useState<TNullable<number>>(null);

  const { kioskData, setBlockDashboardButtonsAvailability } = useKioskStore(
    (state) => ({
      kioskData: state.kioskData,
      setBlockDashboardButtonsAvailability:
        state.setBlockDashboardButtonsAvailability,
    }),
  );
  const timezones = useListStore((state) => state.timezones);

  const checkKioskAvailability = useCallback(async () => {
    if (!kioskData) return;
    const todayDate = getNowInTimezone(kioskData.timezone, timezones);
    if (!todayDate) return;
    const todayWeekday = todayDate.get('day'); // 0 - Sunday, 6 - Saturday
    const weekdaysAvailability = kioskData?.schedule
      ?.map(({ day, startTime, endTime }) => ({
        day: isPropertyOf(day, weekdayStringToDayjsNumberMap)
          ? weekdayStringToDayjsNumberMap[day]
          : null,
        startTime,
        endTime,
      }))
      .filter(({ day }) => day !== null);
    const weekdayMatch = weekdaysAvailability?.filter(
      ({ day }) => day === todayWeekday,
    );
    if (!weekdayMatch?.length) {
      setTime(null);
      setBlockDashboardButtonsAvailability(false);
      return;
    }
    // searching for closest positive endTime in today's intervals
    let initialValue: TNullable<number> = null;
    weekdayMatch.forEach((day) => {
      const buffer = getWallTimeInTimezone(
        todayDate,
        day.endTime,
        kioskData.timezone,
        timezones,
      ).diff(todayDate, 's');
      if (initialValue === null && buffer > 0) {
        initialValue = buffer;
        return;
      }
      if (
        initialValue !== null &&
        buffer <= WARNING_TIME_THRESHOLD &&
        buffer >= 0 &&
        buffer < initialValue
      ) {
        initialValue = buffer;
      }
    });
    if (
      initialValue !== null &&
      initialValue <= WARNING_TIME_THRESHOLD &&
      initialValue >= 0
    ) {
      // unavailable in 15 mins or less
      setTime(initialValue);
      setBlockDashboardButtonsAvailability(true);
    } else {
      setTime(null);
      setBlockDashboardButtonsAvailability(false);
    }
  }, [kioskData, timezones, setBlockDashboardButtonsAvailability]);

  useEffect(() => {
    void checkKioskAvailability();
  }, [checkKioskAvailability]);

  useInterval(() => {
    void checkKioskAvailability();
  }, KIOSK_AVAILABILITY_RECHECK_INTERVAL);

  useInterval(() => {
    if (time === null || time <= 0) return;
    setTime(time - 1);
  }, 1000);

  return time;
};
